/**
 * Endpoint di `/api/v2/`. Una funzione per risorsa, tipata.
 * ---------------------------------------------------------------------------
 * I componenti chiamano SOLO queste funzioni. Percorsi, nomi dei filtri e
 * paginazione stanno qui e in nessun altro posto.
 *
 * Percorsi e parametri sono presi dallo schema OpenAPI reale
 * (`../docs/openapi-v2.yaml`), non ipotizzati.
 *
 * Paginazione: `?pagina=N&per_pagina=M`, risposta `{meta, risultati}`.
 */

import {
  ApiError,
  DATA_SOURCE,
  apiGet,
  apiPost,
  paginatedDa,
  paginatedVuoto,
  sorgenteDati,
} from './client';
import {
  fixtureGetCategoriaItinerario,
  fixtureGetComune,
  fixtureGetEvento,
  fixtureGetItinerario,
  fixtureGetPagina,
  fixtureListCategorieItinerario,
  fixtureListComuni,
  fixtureListEventi,
  fixtureListItinerari,
} from './fixtures';
import type {
  Attivita,
  AttivitaDettaglio,
  Attribuzione,
  Bando,
  CategoriaAttivita,
  CategoriaItinerario,
  ComuneDettaglio,
  ComuneNodo,
  ComuneSintesi,
  DocumentoSfogliabile,
  Evento,
  EventoCalendario,
  Itinerario,
  ItinerarioDettaglio,
  News,
  NonPaginated,
  PaginaRedazionale,
  Paginated,
  PuntoInteresse,
  SiglaProvincia,
  Storia,
  StrutturaRicettivaElenco,
  TipoEvento,
  TipoPuntoInteresse,
  UtenteCorrente,
} from './types';

interface BaseParams {
  readonly signal?: AbortSignal;
  readonly language?: string;
  readonly pagina?: number;
  readonly per_pagina?: number;
  readonly ordering?: string;
  readonly search?: string;
}

/**
 * Spacchetta la risposta di dettaglio del backend reale.
 *
 * Il backend avvolge il dettaglio in `{meta: {attribuzione}, risultato}` —
 * è deliberato (`apps/api/views/pubbliche.py`, `retrieve()`): l'attribuzione
 * per fonte del vincolo 5 deve arrivare anche sul dettaglio, non solo sugli
 * elenchi. Lo schema OpenAPI in `docs/openapi-v2.yaml` dichiara invece
 * l'oggetto nudo: è LO SCHEMA a essere indietro, annotato in
 * `docs/contratto-api-v2.md`.
 *
 * La lettura è tollerante alle due forme, così il frontend non si rompe in
 * nessuna delle due direzioni in cui la discrepanza può risolversi.
 */
function spacchettaDettaglio<T>(data: unknown): T {
  if (
    data !== null &&
    typeof data === 'object' &&
    'risultato' in data &&
    'meta' in data
  ) {
    return (data as { risultato: T }).risultato;
  }
  return data as T;
}

/** Opzioni comuni (segnale + lingua) estratte dai parametri. */
function opzioni(params: BaseParams) {
  return {
    ...(params.signal ? { signal: params.signal } : {}),
    ...(params.language ? { language: params.language } : {}),
  };
}

// ---------------------------------------------------------------------------
// Comuni
// ---------------------------------------------------------------------------

/** Filtri reali di `GET /api/v2/comuni/`: attivo, ordering, piano, provincia, search. */
export interface ListComuniParams extends BaseParams {
  readonly provincia?: SiglaProvincia;
  readonly piano?: 'base' | 'pro';
  readonly attivo?: boolean;
}

export async function listComuni(
  params: ListComuniParams = {},
): Promise<Paginated<ComuneSintesi>> {
  if (DATA_SOURCE === 'fixtures') {
    const tutti = await fixtureListComuni(params.signal);
    let risultati = tutti.risultati;
    if (params.provincia) {
      risultati = risultati.filter((c) => c.provincia === params.provincia);
    }
    if (params.search) {
      // Come l'API reale: la ricerca copre nome E CAP (coda rimisura
      // 31/07/2026 — il blocco «Cerca il tuo comune» di /servizi/).
      const needle = params.search.toLowerCase();
      risultati = risultati.filter(
        (c) =>
          c.nome.toLowerCase().includes(needle) ||
          (c.cap ?? '').toLowerCase().includes(needle),
      );
    }
    return paginatedDa(risultati);
  }
  return apiGet<Paginated<ComuneSintesi>>('/comuni/', {
    ...opzioni(params),
    query: {
      provincia: params.provincia,
      piano: params.piano,
      attivo: params.attivo,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

/**
 * COME si chiede «tutti i comuni in una richiesta sola».
 * ---------------------------------------------------------------------------
 * Punto UNICO dell'applicazione: percorso, forma della busta e ripiego stanno
 * qui e in nessun altro posto. Se il backend rinomina la rotta o i parametri,
 * si corregge questa costante e nient'altro cambia.
 *
 * Il tetto di `per_pagina` è 200 (decisione 202, `apps/api/pagination.py`) e
 * **non si alza**: nel progetto il caso «tutto» ha il suo endpoint dedicato,
 * come `/eventi/calendario/` per gli eventi. Per i comuni è `/comuni/nodi/`
 * (`ComuniViewSet.nodi`, `pagination_class=None`).
 */
const NODI_COMUNI = '/comuni/nodi/';

/** Quante pagine al massimo si ripercorrono nel ripiego qui sotto. 257
 *  comuni a 200 per pagina sono 2: il tetto a 10 è una cintura, non una
 *  previsione — se servisse davvero, il dato non è più «i comuni pugliesi». */
const MAX_PAGINE_RIPIEGO = 10;

/**
 * L'elenco completo dei nodi, con il sistema di riferimento di `x`/`y`.
 *
 * `viewbox` non è un dettaglio: `x` e `y` sono unità del `viewBox` della
 * sagoma SVG, e senza sapere QUALE viewBox i due numeri non sono
 * interpretabili. `null` quando la sorgente non lo dichiara (fixture).
 */
export interface ElencoNodiComuni {
  readonly nodi: readonly ComuneNodo[];
  readonly viewbox: string | null;
}

/**
 * TUTTI i comuni pubblicati, senza paginazione — la fonte di dropdown
 * «Comuni», picker dell'hero e mappa a costellazione (01/09/2026).
 *
 * Fino a questa data quei tre punti leggevano 257 record COMPILATI NEL BUNDLE
 * (`comuni-nodes.json`, estratto dal portale statico): mostravano 257 comuni
 * anche con la base dati vuota, e ogni clic finiva su «Comune non trovato».
 * Ora la fonte è una sola, l'API, e un elenco vuoto è uno stato che si
 * DICHIARA.
 *
 * Il ripiego sulla lista paginata non è pigrizia: frontend e backend si
 * schierano separatamente, e un frontend che resta senza comuni finché non
 * arriva l'altro dipenderebbe dall'ordine dei rilasci. Se `/comuni/nodi/` non
 * risponde (404), si ricompone l'elenco dalle pagine di `/comuni/` — due
 * richieste invece di una, stesse righe, nessuna persa in silenzio. La lista
 * paginata porta gli stessi `x`, `y` e `capoluogo` (sono su
 * `ComuneSintesiSerializer`), quindi il ripiego non è un dato più povero: è
 * lo stesso dato, chiesto in modo più costoso.
 *
 * In modalità fixtures il dataset storico non ha né proiezione né rango:
 * `x`/`y` a `null` e `capoluogo` a `false`. È un limite DICHIARATO della
 * fixture (come i POI senza coordinate), non una posizione inventata.
 */
export async function listNodiComuni(
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<ElencoNodiComuni> {
  // `sorgenteDati()` e non la costante `DATA_SOURCE`: qui la scelta decide
  // se la richiesta parte o no, e con il valore congelato all'import il ramo
  // `api` non sarebbe verificabile in test (vedi il commento in client.ts).
  if (sorgenteDati() === 'fixtures') {
    const tutti = await fixtureListComuni(options.signal);
    return { nodi: tutti.risultati.map(nodoDaSintesi), viewbox: null };
  }

  try {
    const data = await apiGet<unknown>(NODI_COMUNI, options);
    return { nodi: righeNodi(data), viewbox: viewboxDichiarato(data) };
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isNotFound) throw error;
    return { nodi: await nodiDallePagine(options), viewbox: null };
  }
}

/** La riga di elenco vista come nodo: gli stessi campi, un tipo più stretto. */
function nodoDaSintesi(comune: ComuneSintesi): ComuneNodo {
  return {
    slug: comune.slug,
    nome: comune.nome,
    provincia: comune.provincia,
    capoluogo: comune.capoluogo ?? false,
    x: comune.x ?? null,
    y: comune.y ?? null,
    coordinate: comune.coordinate,
  };
}

/** Legge sia l'array nudo sia la busta `{meta, risultati}`. */
function righeNodi(data: unknown): readonly ComuneNodo[] {
  if (Array.isArray(data)) return data as readonly ComuneNodo[];
  if (data && typeof data === 'object' && 'risultati' in data) {
    return (data as { risultati: readonly ComuneNodo[] }).risultati;
  }
  return [];
}

/** `meta.viewbox`, se la busta lo porta. Assente ≠ vuoto: resta `null`. */
function viewboxDichiarato(data: unknown): string | null {
  if (!data || typeof data !== 'object' || !('meta' in data)) return null;
  const meta = (data as { meta?: { viewbox?: unknown } }).meta;
  return typeof meta?.viewbox === 'string' ? meta.viewbox : null;
}

/**
 * Ripiego: la lista paginata, ripercorsa fino all'ultima pagina.
 *
 * Chiama `apiGet` invece di `listComuni` perché qui si è già deciso di
 * parlare con l'API: passare da `listComuni` rientrerebbe nel suo ramo
 * fixtures e mescolerebbe le due sorgenti dentro una richiesta sola.
 */
async function nodiDallePagine(options: {
  signal?: AbortSignal;
  language?: string;
}): Promise<readonly ComuneNodo[]> {
  const righe: ComuneNodo[] = [];
  for (let pagina = 1; pagina <= MAX_PAGINE_RIPIEGO; pagina += 1) {
    const busta = await apiGet<Paginated<ComuneSintesi>>('/comuni/', {
      ...opzioni(options),
      query: { pagina, per_pagina: 200 },
    });
    righe.push(...busta.risultati.map(nodoDaSintesi));
    // `prossima` è l'unico campo che dica se manca qualcosa: `per_pagina` è
    // il valore APPLICATO, non quello richiesto (decisione 202).
    if (!busta.meta.prossima) break;
  }
  return righe;
}

/** `null` se il comune non esiste: 404 è uno stato normale, non un throw. */
export async function getComune(
  slug: string,
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<ComuneDettaglio | null> {
  if (DATA_SOURCE === 'fixtures') {
    return fixtureGetComune(slug, options.signal);
  }
  try {
    const data = await apiGet<unknown>(`/comuni/${encodeURIComponent(slug)}/`, options);
    return spacchettaDettaglio<ComuneDettaglio>(data);
  } catch (error) {
    // `isNotFound` copre sia lo stato 404 sia il codice applicativo
    // `not_found`: un comune inesistente è uno stato normale, non un guasto.
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

/**
 * `GET /api/v2/comuni/{comune_slug}/punti-interesse/` — paginato.
 *
 * `ComuneDettaglio.punti_interesse` porta già l'elenco completo, e per la
 * tabella e la mappa della scheda si usa quello. Questo endpoint serve quando
 * servono paginazione, filtro per tipo o ricerca senza ricaricare l'intera
 * scheda — Brindisi ha 152 POI.
 */
export interface ListPuntiInteresseParams extends BaseParams {
  readonly tipo?: TipoPuntoInteresse;
}

export async function listPuntiInteresse(
  comuneSlug: string,
  params: ListPuntiInteresseParams = {},
): Promise<Paginated<PuntoInteresse>> {
  if (DATA_SOURCE === 'fixtures') {
    const comune = await fixtureGetComune(comuneSlug, params.signal);
    if (!comune) return paginatedVuoto<PuntoInteresse>();
    let risultati = comune.punti_interesse;
    if (params.tipo) risultati = risultati.filter((p) => p.tipo === params.tipo);
    return paginatedDa(risultati);
  }
  return apiGet<Paginated<PuntoInteresse>>(
    `/comuni/${encodeURIComponent(comuneSlug)}/punti-interesse/`,
    {
      ...opzioni(params),
      query: {
        tipo: params.tipo,
        search: params.search,
        ordering: params.ordering,
        pagina: params.pagina,
        per_pagina: params.per_pagina,
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Eventi
// ---------------------------------------------------------------------------

/**
 * Filtri reali di `GET /api/v2/eventi/`:
 * `a`, `comune__slug`, `da`, `ordering`, `pagina`, `per_pagina`, `provincia`,
 * `ricorrenza_mese`, `search`, `solo_data_certa`, `solo_ricorrenti`, `stato`, `tipo`.
 */
export interface ListEventiParams extends BaseParams {
  /** Slug del comune: è così che si ottengono gli eventi di una scheda comune. */
  readonly comune__slug?: string;
  readonly provincia?: SiglaProvincia;
  readonly tipo?: TipoEvento;
  readonly stato?: 'cancelled' | 'draft' | 'published' | 'stub';
  /** Inizio dell'intervallo. */
  readonly da?: string;
  /** Fine dell'intervallo. */
  readonly a?: string;
  /**
   * `true` = solo eventi con data certa. Da usare con cautela: nel dataset
   * storico **362 eventi su 362** non hanno data esatta, quindi questo filtro
   * può svuotare l'elenco. Vedi `QuandoEvento` in `types.ts`.
   */
  readonly solo_data_certa?: boolean;
  readonly solo_ricorrenti?: boolean;
  readonly ricorrenza_mese?: number;
}

export async function listEventi(params: ListEventiParams = {}): Promise<Paginated<Evento>> {
  if (DATA_SOURCE === 'fixtures') {
    const tutti = await fixtureListEventi(params.signal);
    let risultati = tutti.risultati;
    if (params.comune__slug) {
      risultati = risultati.filter((e) => e.comune === params.comune__slug);
    }
    if (params.provincia) {
      risultati = risultati.filter((e) => e.provincia === params.provincia);
    }
    if (params.tipo) {
      risultati = risultati.filter((e) => e.tipo === params.tipo);
    }
    if (params.search) {
      const needle = params.search.toLowerCase();
      risultati = risultati.filter(
        (e) =>
          e.titolo.toLowerCase().includes(needle) ||
          e.comune_nome.toLowerCase().includes(needle) ||
          (e.descrizione ?? '').toLowerCase().includes(needle),
      );
    }
    return paginatedDa(risultati);
  }
  return apiGet<Paginated<Evento>>('/eventi/', {
    ...opzioni(params),
    query: {
      comune__slug: params.comune__slug,
      provincia: params.provincia,
      tipo: params.tipo,
      stato: params.stato,
      da: params.da,
      a: params.a,
      solo_data_certa: params.solo_data_certa,
      solo_ricorrenti: params.solo_ricorrenti,
      ricorrenza_mese: params.ricorrenza_mese,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

/** Filtri reali di `GET /api/v2/eventi/calendario/`: provincia, tipo, comune. */
export interface ListEventiCalendarioParams {
  readonly signal?: AbortSignal;
  readonly language?: string;
  readonly provincia?: SiglaProvincia;
  readonly tipo?: TipoEvento;
  /** Slug del comune (qui è `comune`, non `comune__slug`: contratto 200–204). */
  readonly comune?: string;
}

/**
 * `GET /api/v2/eventi/calendario/` — TUTTI gli eventi pubblicati, senza
 * paginazione, in forma leggera (decisione 205, contratto backend 200–204).
 *
 * Esiste perché la lista paginata tronca al tetto server (200 su 622) anche
 * chiedendo `per_pagina: 500`, e lo dichiara solo in `meta.per_pagina`: un
 * calendario alimentato da una pagina mostrava mesi vuoti che vuoti non sono
 * (agosto: 136 eventi reali, zero visibili). La busta è `{meta, risultati}`
 * con `meta` ridotto a `totale` e `attribuzione` (verificato sulla risposta
 * viva): niente campi di paginazione, perché la paginazione non c'è.
 *
 * In modalità fixtures la lista è già completa e non paginata: si applica
 * solo il filtro, come farebbe il server.
 */
export async function listEventiCalendario(
  params: ListEventiCalendarioParams = {},
): Promise<NonPaginated<EventoCalendario>> {
  if (DATA_SOURCE === 'fixtures') {
    const tutti = await fixtureListEventi(params.signal);
    let risultati: readonly EventoCalendario[] = tutti.risultati;
    if (params.comune) risultati = risultati.filter((e) => e.comune === params.comune);
    if (params.provincia) {
      risultati = risultati.filter((e) => e.provincia === params.provincia);
    }
    if (params.tipo) risultati = risultati.filter((e) => e.tipo === params.tipo);
    return { meta: { totale: risultati.length }, risultati };
  }
  return apiGet<NonPaginated<EventoCalendario>>('/eventi/calendario/', {
    ...(params.signal ? { signal: params.signal } : {}),
    ...(params.language ? { language: params.language } : {}),
    query: {
      provincia: params.provincia,
      tipo: params.tipo,
      comune: params.comune,
    },
  });
}

export async function getEvento(
  slug: string,
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<Evento | null> {
  if (DATA_SOURCE === 'fixtures') return fixtureGetEvento(slug, options.signal);
  try {
    const data = await apiGet<unknown>(`/eventi/${encodeURIComponent(slug)}/`, options);
    return spacchettaDettaglio<Evento>(data);
  } catch (error) {
    // `isNotFound` copre sia lo stato 404 sia il codice applicativo
    // `not_found`: un comune inesistente è uno stato normale, non un guasto.
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Storie
// ---------------------------------------------------------------------------

/** Filtri reali: `categoria`, `comune__slug`, `provincia`, `tipo`, `search`, `ordering`. */
export interface ListStorieParams extends BaseParams {
  readonly comune__slug?: string;
  readonly provincia?: SiglaProvincia;
  readonly categoria?: string;
  readonly tipo?: string;
}

export async function listStorie(params: ListStorieParams = {}): Promise<Paginated<Storia>> {
  if (DATA_SOURCE === 'fixtures') return paginatedVuoto<Storia>();
  return apiGet<Paginated<Storia>>('/storie/', {
    ...opzioni(params),
    query: {
      comune__slug: params.comune__slug,
      provincia: params.provincia,
      categoria: params.categoria,
      tipo: params.tipo,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

/**
 * ⚠️ **`/api/v2/news/` NON ha un filtro per comune.** I parametri reali sono:
 * `archiviato`, `categoria__codice`, `ordering`, `pagina`, `per_pagina`,
 * `search`, `stato`, `territorio` — verificato sullo schema.
 *
 * Finché il backend non chiarisce se `territorio` accetta uno slug di comune
 * (o se va aggiunto `comune__slug`), **la pagina comune non mostra le news**.
 * Non si usa `territorio` a indovinare: produrrebbe elenchi sbagliati senza
 * segnalare nulla, ed è esattamente il "dato inventato" che il vincolo 4
 * vieta. Punto aperto in `../docs/contratto-api-v2.md`.
 */
export interface ListNewsParams extends BaseParams {
  readonly territorio?: string;
  readonly categoria__codice?: string;
  readonly stato?: string;
  readonly archiviato?: boolean;
}

export async function listNews(params: ListNewsParams = {}): Promise<Paginated<News>> {
  if (DATA_SOURCE === 'fixtures') return paginatedVuoto<News>();
  return apiGet<Paginated<News>>('/news/', {
    ...opzioni(params),
    query: {
      territorio: params.territorio,
      categoria__codice: params.categoria__codice,
      stato: params.stato,
      archiviato: params.archiviato,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

// ---------------------------------------------------------------------------
// Bandi e itinerari
// ---------------------------------------------------------------------------

/**
 * ⚠️ **I bandi NON sono associabili ai comuni.**
 *
 * Il filtro `comuni__slug` esiste per coerenza con gli altri endpoint ma
 * restituisce **sempre zero risultati**: la sorgente ha solo un `territorio`
 * testuale (`Puglia`, `Italia`, `Sud Italia`, `UE`, più `Lecce` e `Bari` che
 * sono ambigui fra comune e provincia).
 *
 * Perciò **i bandi non compaiono nella scheda comune**, come le news. Mostrare
 * un elenco vuoto lascerebbe credere che per quel comune non ci sia nulla,
 * quando il problema è che l'associazione non esiste proprio.
 */
export async function listBandi(params: BaseParams = {}): Promise<Paginated<Bando>> {
  if (DATA_SOURCE === 'fixtures') return paginatedVuoto<Bando>();
  return apiGet<Paginated<Bando>>('/bandi/', {
    ...opzioni(params),
    query: {
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

/** `GET /bandi/{slug}/` (alias `/news/{slug}/`) — la rotta SPA usa lo SLUG,
 *  non gli id legacy `n1..n9` (collidono fra le sorgenti). */
export async function getBando(
  slug: string,
  options: { signal?: AbortSignal } = {},
): Promise<Bando | null> {
  if (DATA_SOURCE === 'fixtures') return null;
  try {
    const data = await apiGet<unknown>(`/bandi/${encodeURIComponent(slug)}/`, options);
    return spacchettaDettaglio<Bando>(data);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Attività «Gusta la Puglia» (contratti del 31/07/2026)
// ---------------------------------------------------------------------------

/** Filtri reali di `GET /attivita/`: categoria__codice, comune__slug, stato,
 *  demo; ricerca su nome/indirizzo/descrizione breve. */
export interface ListAttivitaParams extends BaseParams {
  readonly categoria__codice?: string;
  readonly comune__slug?: string;
  readonly stato?: 'importata' | 'approvata';
  readonly demo?: boolean;
}

/**
 * 193 schede reali (pilota Brindisi), base OSM **ODbL 1.0** share-alike:
 * la fonte `gusta_osm` di `meta.attribuzione` va MOSTRATA (vincolo 5).
 * Nessun rating o classifica: scelta editoriale della sorgente.
 */
export async function listAttivita(
  params: ListAttivitaParams = {},
): Promise<Paginated<Attivita>> {
  if (DATA_SOURCE === 'fixtures') return paginatedVuoto<Attivita>();
  return apiGet<Paginated<Attivita>>('/attivita/', {
    ...opzioni(params),
    query: {
      categoria__codice: params.categoria__codice,
      comune__slug: params.comune__slug,
      stato: params.stato,
      demo: params.demo,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

export async function getAttivita(
  slug: string,
  options: { signal?: AbortSignal } = {},
): Promise<AttivitaDettaglio | null> {
  if (DATA_SOURCE === 'fixtures') return null;
  try {
    const data = await apiGet<unknown>(`/attivita/${encodeURIComponent(slug)}/`, options);
    return spacchettaDettaglio<AttivitaDettaglio>(data);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

/** `GET /attivita/categorie/` — le 15 categorie approvate, risposta piatta. */
export async function listCategorieAttivita(
  options: { signal?: AbortSignal } = {},
): Promise<readonly CategoriaAttivita[]> {
  if (DATA_SOURCE === 'fixtures') return [];
  return apiGet<readonly CategoriaAttivita[]>('/attivita/categorie/', options);
}

// ---------------------------------------------------------------------------
// Documenti sfogliabili (flipbook — contratto del 31/07/2026)
// ---------------------------------------------------------------------------

/** `GET /documenti/{slug}/` — es. `mappa-multimediale-brindisi`. Le immagini
 *  sono sotto `/media/…` (servite da nginx). Licenza PROPRIETARIA: non è un
 *  dato aperto e l'attribuzione lo dice. */
export async function getDocumento(
  slug: string,
  options: { signal?: AbortSignal } = {},
): Promise<DocumentoSfogliabile | null> {
  if (DATA_SOURCE === 'fixtures') return null;
  try {
    const data = await apiGet<unknown>(`/documenti/${encodeURIComponent(slug)}/`, options);
    return spacchettaDettaglio<DocumentoSfogliabile>(data);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

/**
 * `GET /api/v2/strutture-ricettive/` — elenco trasversale per la pagina-dato
 * /territorio/mare-e-lidi/ (coda rimisura 31/07/2026): i 436 stabilimenti
 * Pugliapromozione risolti a un comune (441 in sorgente, 5 scartati e
 * DICHIARATI dal backend). I flag di accessibilità sono tri-state:
 * `null` = non dichiarato, e il filtro li lascia fuori solo se richiesto.
 *
 * In modalità fixtures il dataset non esiste (le schede comune del dump
 * vecchio hanno gli array vuoti): elenco vuoto DICHIARATO, non simulato.
 */
export interface ListStruttureRicettiveParams extends BaseParams {
  readonly categoria?: string;
  readonly comune__slug?: string;
  readonly comune__provincia?: SiglaProvincia;
  readonly accessibilita_interna?: boolean;
  readonly accessibilita_esterna?: boolean;
}

export async function listStruttureRicettive(
  params: ListStruttureRicettiveParams = {},
): Promise<Paginated<StrutturaRicettivaElenco>> {
  if (DATA_SOURCE === 'fixtures') return paginatedVuoto<StrutturaRicettivaElenco>();
  return apiGet<Paginated<StrutturaRicettivaElenco>>('/strutture-ricettive/', {
    ...opzioni(params),
    query: {
      categoria: params.categoria,
      comune__slug: params.comune__slug,
      comune__provincia: params.comune__provincia,
      accessibilita_interna: params.accessibilita_interna,
      accessibilita_esterna: params.accessibilita_esterna,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

/**
 * Filtri di `GET /api/v2/itinerari/` (§I.1).
 *
 * `categoria_tappa` seleziona gli itinerari che contengono almeno una tappa di
 * quella categoria e **non filtra le tappe restituite**: il filtro dentro la
 * scheda resta del client, perché filtrare la sequenza lato server cambierebbe
 * i cumulati.
 */
export interface ListItinerariParams extends BaseParams {
  /** Slug della categoria DELL'ITINERARIO: il filtro di `/itinerari/<cat>/`. */
  readonly categoria?: string;
  /** Slug di un comune attraversato (almeno una tappa lì). */
  readonly comune?: string;
  readonly categoria_tappa?: string;
  readonly tono?: 'istituzionale' | 'divulgativo';
}

export async function listItinerari(
  params: ListItinerariParams = {},
): Promise<Paginated<Itinerario>> {
  if (DATA_SOURCE === 'fixtures') {
    return fixtureListItinerari(
      { ...(params.categoria !== undefined ? { categoria: params.categoria } : {}) },
      params.signal,
    );
  }
  return apiGet<Paginated<Itinerario>>('/itinerari/', {
    ...opzioni(params),
    query: {
      categoria: params.categoria,
      comune: params.comune,
      categoria_tappa: params.categoria_tappa,
      tono: params.tono,
      search: params.search,
      ordering: params.ordering,
      pagina: params.pagina,
      per_pagina: params.per_pagina,
    },
  });
}

/**
 * Il dettaglio con la sua ATTRIBUZIONE.
 *
 * Non è un capriccio di forma: un itinerario mescola licenze — beni MiC in
 * CC BY 4.0 e attività su base OpenStreetMap in ODbL, che è share-alike — e
 * `meta.attribuzione` è l'unico posto dove arrivano i testi di attribuzione
 * già pronti. `tappe[].luogo.fonte` porta la CHIAVE della fonte
 * (`gusta_osm`), non la frase da mostrare: renderla al pubblico sarebbe
 * mostrare un identificatore al posto di un credito (vincolo 5).
 *
 * Per questo qui NON si usa `spacchettaDettaglio`, che tiene solo
 * `risultato`: la busta serve tutta.
 */
export interface DettaglioItinerario {
  readonly itinerario: ItinerarioDettaglio;
  readonly attribuzione: readonly Attribuzione[];
}

export async function getItinerario(
  slug: string,
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<DettaglioItinerario | null> {
  // La fixture è una cattura della busta intera, quindi porta anche
  // l'attribuzione: la filiera a fixture verifica il vincolo 5 come quella
  // vera, invece di essere l'unico ramo in cui i crediti non si vedono.
  if (DATA_SOURCE === 'fixtures') return fixtureGetItinerario(slug, options.signal);
  try {
    const data = await apiGet<unknown>(`/itinerari/${encodeURIComponent(slug)}/`, options);
    const busta = data as {
      risultato?: ItinerarioDettaglio;
      meta?: { attribuzione?: readonly Attribuzione[] };
    };
    return {
      itinerario: spacchettaDettaglio<ItinerarioDettaglio>(data),
      attribuzione: busta.meta?.attribuzione ?? [],
    };
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

/**
 * `GET /api/v2/itinerari/categorie/` — risposta **piatta e non paginata**,
 * come `/attivita/categorie/`.
 *
 * Le categorie con `itinerari_totali: 0` ci sono comunque: nasconderle farebbe
 * sparire dalla navigazione una categoria appena creata e non ancora popolata,
 * che è esattamente il momento in cui la redazione la guarda.
 */
export async function listCategorieItinerario(
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<readonly CategoriaItinerario[]> {
  if (DATA_SOURCE === 'fixtures') return fixtureListCategorieItinerario(options.signal);
  const data = await apiGet<unknown>('/itinerari/categorie/', options);
  if (Array.isArray(data)) return data as readonly CategoriaItinerario[];
  // Tolleranza alla forma paginata: lo schema dichiara l'array nudo, ma una
  // risposta impaginata non deve far sparire il menu delle categorie.
  if (data && typeof data === 'object' && 'risultati' in data) {
    return (data as { risultati: readonly CategoriaItinerario[] }).risultati;
  }
  return [];
}

/** `GET /api/v2/itinerari/categorie/{slug}/` — intestazione di `/itinerari/<cat>/`. */
export async function getCategoriaItinerario(
  slug: string,
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<CategoriaItinerario | null> {
  if (DATA_SOURCE === 'fixtures') return fixtureGetCategoriaItinerario(slug, options.signal);
  try {
    const data = await apiGet<unknown>(
      `/itinerari/categorie/${encodeURIComponent(slug)}/`,
      options,
    );
    return spacchettaDettaglio<CategoriaItinerario>(data);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Pagine redazionali (decisioni 28–42)
// ---------------------------------------------------------------------------

/**
 * `GET /api/v2/pagine/{slug}/?lang=xx`.
 *
 * La risposta porta `lingua` (della traduzione servita) e `tradotta`: quando
 * `tradotta` è `false` il backend sta servendo il fallback italiano e la
 * pagina deve dirlo (decisione 36). Solo le pagine PUBBLICATE esistono qui:
 * un 404 può voler dire "slug sbagliato" oppure "ancora in bozza", e per il
 * pubblico è la stessa cosa.
 *
 * In modalità fixtures le bozze vengono servite come pubblicate — vedi
 * l'avviso in `fixtures.ts`: vale solo in sviluppo.
 */
export async function getPagina(
  slug: string,
  options: { signal?: AbortSignal; language?: string } = {},
): Promise<PaginaRedazionale | null> {
  const lingua = options.language?.slice(0, 2).toLowerCase() ?? 'it';
  if (DATA_SOURCE === 'fixtures') {
    return fixtureGetPagina(slug, lingua, options.signal);
  }
  try {
    const data = await apiGet<unknown>(`/pagine/${encodeURIComponent(slug)}/`, {
      ...(options.signal ? { signal: options.signal } : {}),
      query: { lang: lingua },
    });
    return spacchettaDettaglio<PaginaRedazionale>(data);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Fonti — per l'attribuzione (vincolo 5)
// ---------------------------------------------------------------------------

/**
 * `GET /api/v2/fonti/`.
 *
 * Sulle righe il backend mette `fonte` come **stringa** (un codice); nome,
 * ente, licenza e URL stanno qui. Il join lo fa il frontend: le fonti sono
 * poche e cambiano di rado, mentre ripetere licenza e URL su ogni riga
 * gonfierebbe le risposte (Brindisi: 152 POI + 98 scuole).
 *
 * `Attribuzione` è ora un componente **definito** nello schema, con `licenza`
 * tipizzata (`Licenza`) e `campi` che dice **quali campi** del risultato
 * provengono da quella fonte: è ciò che rende possibile l'attribuzione per
 * campo del vincolo 5.
 *
 * Su errore restituisce `[]`: l'attribuzione mancante è un difetto da
 * segnalare, non un motivo per non mostrare il contenuto.
 */
let fontiCache: Promise<readonly Attribuzione[]> | null = null;

export async function listFonti(signal?: AbortSignal): Promise<readonly Attribuzione[]> {
  if (DATA_SOURCE === 'fixtures') return [];
  fontiCache ??= apiGet<unknown>('/fonti/', { ...(signal ? { signal } : {}) })
    .then((data): readonly Attribuzione[] => {
      // Si accetta sia l'array nudo sia la busta paginata.
      if (Array.isArray(data)) return data as readonly Attribuzione[];
      const busta = data as Partial<Paginated<Attribuzione>>;
      return busta?.risultati ?? [];
    })
    .catch(() => []);
  return fontiCache;
}

export function resetFontiCache(): void {
  fontiCache = null;
}

/**
 * Indicizza le fonti per codice, per il join con il campo `fonte` delle voci.
 * `meta.attribuzione` copre già i risultati della pagina corrente; questa
 * mappa serve dove l'attribuzione va mostrata accanto a una singola riga.
 */
export function indicizzaFonti(
  fonti: readonly Attribuzione[],
): ReadonlyMap<string, Attribuzione> {
  return new Map(fonti.map((f) => [f.fonte, f]));
}

// ---------------------------------------------------------------------------
// Contributi (partecipazione) e area riservata
// ---------------------------------------------------------------------------

/**
 * `POST /api/v2/contributi/` — invio di una proposta (evento, attività,
 * correzione). Anti-bot: honeypot + tempo minimo di compilazione, nessun
 * captcha (vincolo 6). I due campi vanno nel corpo insieme ai dati.
 */
export async function inviaContributo(
  payload: Readonly<Record<string, unknown>>,
  options: { signal?: AbortSignal } = {},
): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/contributi/', payload, options);
}

/**
 * `GET /api/v2/health/` — stato del servizio e conteggi dei dataset.
 * Lo schema dichiara `additionalProperties: {}`: la forma è libera, la
 * pagina di stato mostra le coppie chiave/valore DICHIARATE dal servizio
 * senza presumere campi. In modalità fixtures non c'è nulla da misurare:
 * `null`, e la pagina lo dice (vincolo 4: niente stati inventati).
 */
export async function getHealth(
  options: { signal?: AbortSignal } = {},
): Promise<Record<string, unknown> | null> {
  if (DATA_SOURCE === 'fixtures') return null;
  return apiGet<Record<string, unknown>>('/health/', options);
}

/** `GET /api/v2/auth/io/` — utente corrente. Richiede il token. */
export async function getUtenteCorrente(
  options: { signal?: AbortSignal } = {},
): Promise<UtenteCorrente | null> {
  try {
    return await apiGet<UtenteCorrente>('/auth/io/', { ...options, authenticated: true });
  } catch (error) {
    // Non autenticato o ruolo insufficiente: "nessun utente", non un errore.
    if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) {
      return null;
    }
    throw error;
  }
}
